import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import mcbLogo from "../images/mcb-logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const MANIFEST_API = `${API_BASE}/manifest`;

function toTitle(v) {
  return String(v || "").replace(/_/g, " ").replace(/-/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ManifestPrint() {
  const { id } = useParams();
  const [sailing, setSailing] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${MANIFEST_API}/${id}`, { cache: "no-store" });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Could not load manifest");
        setSailing(data.sailing);
        setBookings((data.bookings || []).filter(b => String(b.status || "").toLowerCase() !== "cancelled"));
        setTotals(data.totals);
      } catch (e) {
        setError(e.message || "Could not load this manifest.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Loading manifest...</div>;
  if (error || !sailing) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>{error || "Sailing not found."}</div>;

  const livestockRows = bookings.flatMap(b =>
    (b.livestockItems || []).map((item, i) => ({ booking: b, item, key: `${b._id}-live-${i}` }))
  );
  const freightRows = bookings.flatMap(b =>
    (b.freightItems || []).map((item, i) => ({ booking: b, item, key: `${b._id}-freight-${i}` }))
  );

  const sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: "#1a1a1a",
    borderBottom: "1px solid #eee",
    paddingBottom: 6,
    marginBottom: 10,
  };
  const thStyle = {
    padding: "8px 10px",
    textAlign: "left",
    fontWeight: 700,
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  };
  const tdStyle = { padding: "8px 10px" };

  return (
    <div style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", background: "#fff", color: "#1a1a1a", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 14mm 16mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
        .print-avoid-break { break-inside: avoid; page-break-inside: avoid; }
        .print-section tr { break-inside: avoid; page-break-inside: avoid; }
      `}</style>

      {/* Screen toolbar */}
      <div className="no-print" style={{ background: "#1a1a1a", padding: "12px 32px", display: "flex", alignItems: "center", gap: 16 }}>
        <Link to={`/dashboard/sailings/${id}`} style={{ color: "#fff", fontSize: 13, textDecoration: "none", opacity: 0.7 }}>← Back to Sailing</Link>
        <button onClick={() => window.print()} style={{ marginLeft: "auto", background: "#7a1428", color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Print / Save PDF
        </button>
      </div>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* ── HEADER ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1a1a1a", paddingBottom: 16, marginBottom: 20 }}>
          <div>
            <img src={mcbLogo} alt="McCallum Bros Ltd" style={{ height: 44, width: "auto" }} />
            <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>747 Rosebank Road, Avondale, Auckland</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>VOYAGE MANIFEST</div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{bookings.length} booking{bookings.length === 1 ? "" : "s"}</div>
            <div style={{ fontSize: 11, color: "#666" }}>Generated: {fmt(new Date())}</div>
          </div>
        </div>

        {/* ── VOYAGE DETAILS ── */}
        <div>
          <div style={sectionTitleStyle}>Voyage Details</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 20 }}>
            <tbody>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700, width: "16%" }}>Sailing Ref</td>
                <td style={tdStyle}>{sailing.sailingRef}</td>
                <td style={{ ...tdStyle, fontWeight: 700, width: "16%" }}>Vessel</td>
                <td style={tdStyle}>{sailing.vesselName || sailing.vessel?.name || "—"}</td>
              </tr>
              <tr style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Route</td>
                <td style={tdStyle}>{sailing.origin} → {sailing.destination}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>ETA</td>
                <td style={tdStyle}>{fmt(sailing.eta)}</td>
              </tr>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Status</td>
                <td style={tdStyle}>{toTitle(sailing.status)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>ETD</td>
                <td style={tdStyle}>{fmt(sailing.etd)}</td>
              </tr>
              <tr style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Total Weight</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{Number(totals?.weightKg || 0).toLocaleString()} kg</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Total Cube</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{Number(totals?.cubeM3 || 0).toLocaleString()} m³</td>
              </tr>
              <tr style={{ background: "#fafafa" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Livestock Head</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{Number(totals?.livestockHead || 0).toLocaleString()}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Total Bookings</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{bookings.length}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── LIVESTOCK MANIFEST ── */}
        {livestockRows.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>Livestock Manifest</div>
            <table className="print-section" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "#1a1a1a", color: "#fff" }}>
                  <th style={thStyle}>Booking</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Class</th>
                  <th style={thStyle}>Head</th>
                  <th style={thStyle}>Avg Weight</th>
                  <th style={thStyle}>Total Weight</th>
                  <th style={thStyle}>Manifest Status</th>
                </tr>
              </thead>
              <tbody>
                {livestockRows.map(({ booking, item, key }, i) => {
                  const head = Number(item.headCount || 0);
                  const avg = Number(item.averageWeightKg || 0);
                  return (
                    <tr key={key} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{booking.bookingRef}</td>
                      <td style={tdStyle}>{booking.customerName}</td>
                      <td style={{ ...tdStyle, textTransform: "capitalize" }}>{item.animalClass || item.livestockType || "—"}</td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{head}</td>
                      <td style={tdStyle}>{avg} kg</td>
                      <td style={tdStyle}>{(head * avg).toLocaleString()} kg</td>
                      <td style={tdStyle}>{toTitle(booking.manifestStatus || "Pending")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── FREIGHT MANIFEST ── */}
        {freightRows.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>Freight Manifest</div>
            <table className="print-section" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "#1a1a1a", color: "#fff" }}>
                  <th style={thStyle}>Booking</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Qty</th>
                  <th style={thStyle}>Weight</th>
                  <th style={thStyle}>Cube</th>
                  <th style={thStyle}>Temp</th>
                  <th style={thStyle}>Manifest Status</th>
                </tr>
              </thead>
              <tbody>
                {freightRows.map(({ booking, item, key }, i) => (
                  <tr key={key} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{booking.bookingRef}</td>
                    <td style={tdStyle}>{booking.customerName}</td>
                    <td style={tdStyle}>{item.description || toTitle(item.freightType) || "Freight"}</td>
                    <td style={tdStyle}>{item.quantity || "—"}</td>
                    <td style={tdStyle}>{Number(item.weightKg || 0).toLocaleString()} kg</td>
                    <td style={tdStyle}>{Number(item.cubeM3 || 0).toLocaleString()} m³</td>
                    <td style={{ ...tdStyle, textTransform: "capitalize" }}>{item.temperatureControl && item.temperatureControl !== "ambient" ? item.temperatureControl : "—"}</td>
                    <td style={tdStyle}>{toTitle(booking.manifestStatus || "Pending")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── SIGN OFF ── */}
        <div className="print-avoid-break">
          <div style={{ border: "1px solid #ddd", borderRadius: 4, padding: 20 }}>
            <div style={{ ...sectionTitleStyle, borderBottom: "none", marginBottom: 16 }}>Sign Off</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {[
                { label: "Prepared By", sub: "Name & Signature" },
                { label: "Approved By — Master / Skipper", sub: "Name & Signature" },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 8 }}>{item.label}</div>
                  <div style={{ borderBottom: "1px solid #1a1a1a", height: 36, marginBottom: 6 }} />
                  <div style={{ fontSize: 9, color: "#bbb" }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", fontSize: 9, color: "#bbb" }}>
          <span>McCallum Bros Ltd · Voyage Manifest · {sailing.sailingRef}</span>
          <span>Generated {new Date().toLocaleString("en-NZ")}</span>
        </div>

      </div>
    </div>
  );
}